import { useMemo, useRef } from 'react';
import { fallbackLayoutPosition } from './flowchartLayout/isolatedSpawnPositions';
import { isNoteNode, isNoteParkedBesideHost } from '../utils/noteUtils';

const ELK_VS_SAVED_JUMP_PX = 2;
const SAVED_STABLE_EPS = 1.5;

/**
 * useNodePositions Hook
 * 
 * Computes effective node positions by merging:
 * - autoPositions (calculated layout)
 * - task.x/y (saved positions from database)
 * - customPositions (runtime overrides from dragging)
 * 
 * Single-campaign priority (simplified):
 * - customPositions
 * - When filtering: task.x/y over auto (filter "flying around" fix)
 * - When ELK just committed new auto while sprint task props are still stale (e.g. right after
 *   Tidy, before persist): committed ELK wins so edges match pathD and debounced interactive
 *   reroute does not fire a second geometry pass. The frame where `useAutoPositions` flips from
 *   true→false is included so small ELK deltas still prefer auto over stale saved props.
 * - Else: saved task.x/y when not in layout animation, then ELK auto fallbacks.
 * During layout animation (useAutoPositions), task.x/y is skipped so ELK frames drive motion.
 * Manual drags update task.x/y in the same render path before ELK re-runs, so "ELK jumped,
 * task unchanged vs last frame" is false and saved coordinates still win after drag.
 * 
 * IMPORTANT: When filtering, we prioritize task.x/y over autoPositions to prevent
 * the "flying around" bug. autoPositions may be calculated for a different set of
 * tasks (e.g., a previous filter state), so task.x/y is the source of truth.
 */
export function useNodePositions({
  tasks,
  autoPositions,
  customPositions,
  isMultiCampaignMode,
  useAutoPositions,
  hasActiveFilters = false,
  elkPositionsOk = false,
}) {
  const prevTaskPropsRef = useRef({});
  const prevAutoPropsRef = useRef({});
  /** Task ids where we render ELK auto until sprint `task.x/y` catches up (Tidy persist lag). */
  const elkDisplayStickyUntilTaskAlignedRef = useRef(new Set());
  /** Tracks last render's `useAutoPositions` to detect the frame layout animation ends. */
  const prevUseAutoPositionsRef = useRef(false);

  return useMemo(() => {
    const droppedLayoutAnim = prevUseAutoPositionsRef.current && !useAutoPositions;
    // Start with an empty positions object
    // We'll build it up from the most authoritative sources
    const positions = {};
    // Always store fresh { x, y } objects — never reuse refs from custom/auto/task
    // so parent in-place mutations cannot desync MemoizedTaskNode vs HandlesLayer.
    const snap = (p) =>
      p && Number.isFinite(p.x) && Number.isFinite(p.y) ? { x: p.x, y: p.y } : null;
    
    if (isMultiCampaignMode) {
      // In multi-campaign mode, start with autoPositions
      Object.keys(autoPositions || {}).forEach((taskId) => {
        const p = snap(autoPositions[taskId]);
        if (p) positions[taskId] = p;
      });
      // Then apply customPositions for immediate visual feedback
      // (e.g., when dragging nodes or after Tidy)
      Object.keys(customPositions || {}).forEach((taskId) => {
        const p = snap(customPositions[taskId]);
        if (p) positions[taskId] = p;
      });
      return positions;
    }
    
    // Single-campaign mode: merge custom, saved task coords, ELK auto, and fallbacks.
    // customPositions are runtime overrides (e.g. in-flight drag).
    // task.x/y are persisted coordinates (must beat stale ELK when not animating — see file header).

    const preferElkAutoOverSaved =
      !isMultiCampaignMode &&
      !hasActiveFilters &&
      (elkPositionsOk || Object.keys(autoPositions || {}).length > 0);

    const isValidPosition = (pos) => {
      return pos && Number.isFinite(pos.x) && Number.isFinite(pos.y);
    };

    const prevTaskById = prevTaskPropsRef.current;
    const prevAutoById = prevAutoPropsRef.current;
    const stickyElkIds = elkDisplayStickyUntilTaskAlignedRef.current;
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    const savedPositions = {};
    tasks.forEach((t) => {
      if (Number.isFinite(t.x) && Number.isFinite(t.y)) {
        savedPositions[t.id] = { x: t.x, y: t.y };
      }
    });
    for (const id of [...stickyElkIds]) {
      const task = taskById.get(id);
      const a = autoPositions[id];
      if (!task || !Number.isFinite(task.x) || !Number.isFinite(task.y) || !isValidPosition(a)) {
        stickyElkIds.delete(id);
        continue;
      }
      const aligned =
        Math.abs(task.x - a.x) <= SAVED_STABLE_EPS && Math.abs(task.y - a.y) <= SAVED_STABLE_EPS;
      const pt = prevTaskById[id];
      const taskMoved =
        !pt ||
        Math.abs(pt.x - task.x) > SAVED_STABLE_EPS ||
        Math.abs(pt.y - task.y) > SAVED_STABLE_EPS;
      if (aligned || taskMoved) stickyElkIds.delete(id);
    }
    
    // For each task, determine its position using the priority order
    let fallbackIndex = 0;
    tasks.forEach(task => {
      // Check customPositions first (highest priority)
      if (customPositions[task.id] && isValidPosition(customPositions[task.id])) {
        const p = snap(customPositions[task.id]);
        if (p) {
          positions[task.id] = p;
          return;
        }
      }

      // Notes saved in the old host-right slot sit on the next mission.
      // Prefer ELK when it has a real coordinate for that note.
      if (
        isNoteNode(task)
        && isValidPosition(autoPositions[task.id])
        && isNoteParkedBesideHost(task, tasks, savedPositions)
      ) {
        const p = snap(autoPositions[task.id]);
        if (p) {
          positions[task.id] = p;
          return;
        }
      }

      // Filtered view: saved coordinates stay authoritative
      if (hasActiveFilters && !useAutoPositions && task.x !== undefined && task.y !== undefined) {
        const savedPos = { x: task.x, y: task.y };
        if (isValidPosition(savedPos)) {
          positions[task.id] = savedPos;
          return;
        }
      }

      // Tidy / ELK settle: sprint props often lag behind committed ELK until persist runs.
      // Prefer ELK for display when (a) auto just jumped but task props did not, or (b) we are
      // already holding this id sticky until task aligns. Drag updates task vs last frame, so
      // "task unchanged vs last frame" is false and saved coordinates still win after drag.
      if (
        !isMultiCampaignMode &&
        !hasActiveFilters &&
        elkPositionsOk &&
        !useAutoPositions &&
        autoPositions[task.id] &&
        isValidPosition(autoPositions[task.id])
      ) {
        const curAuto = autoPositions[task.id];
        const prevAuto = prevAutoById[task.id];
        const prevTask = prevTaskById[task.id];
        const autoJumped =
          prevAuto &&
          isValidPosition(prevAuto) &&
          (Math.abs(prevAuto.x - curAuto.x) > ELK_VS_SAVED_JUMP_PX ||
            Math.abs(prevAuto.y - curAuto.y) > ELK_VS_SAVED_JUMP_PX);
        const taskPropsStableVersusPrevFrame =
          !prevTask ||
          (Number.isFinite(task.x) &&
            Number.isFinite(task.y) &&
            Math.abs(prevTask.x - task.x) <= SAVED_STABLE_EPS &&
            Math.abs(prevTask.y - task.y) <= SAVED_STABLE_EPS);
        const taskDivergesFromAuto =
          Number.isFinite(task.x) &&
          Number.isFinite(task.y) &&
          (Math.abs(task.x - curAuto.x) > SAVED_STABLE_EPS ||
            Math.abs(task.y - curAuto.y) > SAVED_STABLE_EPS);
        const useStickyElk =
          taskDivergesFromAuto &&
          taskPropsStableVersusPrevFrame &&
          (autoJumped || stickyElkIds.has(task.id) || droppedLayoutAnim);
        if (useStickyElk) {
          const p = snap(curAuto);
          if (p) {
            positions[task.id] = p;
            stickyElkIds.add(task.id);
            return;
          }
        }
      }

      // Persisted sprint coordinates (skipped only while layout animation is driving ELK frames)
      if (!useAutoPositions && task.x !== undefined && task.y !== undefined) {
        const savedPos = { x: task.x, y: task.y };
        if (isValidPosition(savedPos)) {
          positions[task.id] = savedPos;
          return;
        } else {
          console.warn('[useNodePositions] Task has invalid saved position:', task.id, savedPos);
        }
      }

      if (
        preferElkAutoOverSaved &&
        autoPositions[task.id] &&
        isValidPosition(autoPositions[task.id])
      ) {
        const p = snap(autoPositions[task.id]);
        if (p) {
          positions[task.id] = p;
          return;
        }
      }
      
      // Fall back to autoPositions
      if (autoPositions[task.id]) {
        if (isValidPosition(autoPositions[task.id])) {
          const p = snap(autoPositions[task.id]);
          if (p) positions[task.id] = p;
        } else {
          positions[task.id] = fallbackLayoutPosition(fallbackIndex);
          fallbackIndex += 1;
        }
      } else if (Number.isFinite(task.x) && Number.isFinite(task.y)) {
        // ELK has not placed this card yet. Keep the spawn next to the (+)
        // instead of parking it on the top-left fallback grid.
        positions[task.id] = { x: task.x, y: task.y };
      } else {
        // No autoPosition yet (fresh paste, ELK still running). Spread so a
        // large paste cannot stack every card at 100,100 and swallow clicks.
        positions[task.id] = fallbackLayoutPosition(fallbackIndex);
        fallbackIndex += 1;
      }
    });

    const nextPrevTask = {};
    const nextPrevAuto = {};
    tasks.forEach((task) => {
      if (Number.isFinite(task.x) && Number.isFinite(task.y)) {
        nextPrevTask[task.id] = { x: task.x, y: task.y };
      }
      const a = autoPositions[task.id];
      if (isValidPosition(a)) {
        nextPrevAuto[task.id] = { x: a.x, y: a.y };
      }
    });
    prevTaskPropsRef.current = nextPrevTask;
    prevAutoPropsRef.current = nextPrevAuto;
    prevUseAutoPositionsRef.current = useAutoPositions;

    return positions;
  }, [autoPositions, customPositions, tasks, isMultiCampaignMode, useAutoPositions, hasActiveFilters, elkPositionsOk]);
}

export default useNodePositions;
